import { useEffect, useState, useCallback } from "react";
import { Loader2, Package, X, ExternalLink } from "lucide-react";
import { api } from "@/lib/api";
import { OVERLINE, H1, useAdmin } from "@/components/admin/ui";

const money = (c) => (c == null ? "—" : new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(c / 100));
const TABS = [["", "All"], ["pending", "Pending"], ["on_hold", "On hold"], ["submitted", "Submitted"], ["error", "Errors"]];
const STATUS_CLS = {
  pending: "bg-[#8A5A44]/15 text-[#8A5A44]", on_hold: "bg-[#B8912E]/15 text-[#8A6D1F]",
  submitted: "bg-[#2E7D46]/15 text-[#2E7D46]", error: "bg-[#B04A3A]/15 text-[#B04A3A]",
};
const StatusPill = ({ s }) => <span className={`text-[10px] font-semibold uppercase tracking-wide px-2 py-0.5 ${STATUS_CLS[s] || "bg-[#E5E2DC] text-[#5C6B61]"}`}>{(s || "").replace("_", " ") || "—"}</span>;

function OrderDrawer({ sid, onClose }) {
  const [o, setO] = useState(null);
  useEffect(() => { api.admOrderDetail(sid).then(setO).catch(() => setO(false)); }, [sid]);
  const a = o?.address || {};
  return (
    <div className="fixed inset-0 z-[95] flex justify-end" data-testid="order-drawer">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative w-full max-w-md bg-white h-full overflow-auto shadow-2xl">
        <div className="flex items-center justify-between px-5 py-4 border-b border-[#E5E2DC] sticky top-0 bg-white">
          <h2 className="font-serif text-xl text-[#1F2A22]">Order details</h2>
          <button onClick={onClose} data-testid="order-drawer-close"><X size={20} /></button>
        </div>
        {!o ? <div className="py-20 grid place-items-center"><Loader2 className="animate-spin text-[#FF6A00]" /></div> : o === false ? <p className="p-6 text-[#B04A3A]">Not found.</p> : (
          <div className="p-5 space-y-4 text-sm">
            <div className="flex items-center justify-between"><span className="font-mono text-xs text-[#8A8478]">{o.session_id}</span><StatusPill s={o.status} /></div>
            <div className="space-y-1">
              {(o.items_summary || []).map((it, i) => (
                <div key={i} className="flex items-center gap-3">
                  {it.image && <img src={it.image} alt="" className="w-10 h-10 object-cover border border-[#E5E2DC]" />}
                  <span className="flex-1 text-[#1F2A22]">{it.title} × {it.quantity}</span>
                  <span className="text-[#5C6B61]">{money(it.price * it.quantity)}</span>
                </div>
              ))}
            </div>
            <div className="border-t border-[#E5E2DC] pt-3 space-y-1">
              <div className="flex justify-between"><span className="text-[#5C6B61]">Subtotal</span><span>{money(o.subtotal_cents)}</span></div>
              {o.discount_cents > 0 && <div className="flex justify-between text-[#2E7D46]"><span>Discount {o.discount_code ? `(${o.discount_code})` : ""}</span><span>−{money(o.discount_cents)}</span></div>}
              <div className="flex justify-between"><span className="text-[#5C6B61]">Shipping charged</span><span>{o.free_shipping ? "Free" : money(o.shipping_cents)}</span></div>
              {o.printify_shipping_cents != null && <div className="flex justify-between text-[#8A8478]"><span>Printify shipping cost</span><span>{money(o.printify_shipping_cents)}</span></div>}
              {o.shipping_absorbed_cents > 0 && <div className="flex justify-between text-[#B04A3A]"><span>Shipping absorbed</span><span>{money(o.shipping_absorbed_cents)}</span></div>}
              <div className="flex justify-between font-semibold text-[#1F2A22] pt-1 border-t border-[#F0EEE9]"><span>Total charged</span><span>{money(o.total_cents)}</span></div>
            </div>
            <div className="border-t border-[#E5E2DC] pt-3">
              <p className="text-[11px] uppercase tracking-wide text-[#8A8478]">Ship to</p>
              <p className="text-[#1F2A22] mt-1">{a.first_name} {a.last_name}</p>
              <p className="text-[#5C6B61]">{a.address1}{a.address2 ? `, ${a.address2}` : ""}</p>
              <p className="text-[#5C6B61]">{a.city}, {a.region} {a.zip} {a.country}</p>
              <p className="text-[#5C6B61]">{o.email}</p>
            </div>
            <div className="border-t border-[#E5E2DC] pt-3 space-y-1 text-xs text-[#5C6B61]">
              <div className="flex justify-between"><span>Printify order</span><span>{o.printify_order_id || "—"}</span></div>
              <div className="flex justify-between"><span>Stripe mode</span><span>{o.mode || "—"}</span></div>
              {o.payment_status && <div className="flex justify-between"><span>Payment</span><span>{o.payment_status}</span></div>}
              {o.return_request && <div className="flex justify-between text-[#8A5A44]"><span>Return</span><span>{o.return_request.status}</span></div>}
            </div>
            {o.error && <p className="text-xs text-[#B04A3A] flex items-center gap-1"><ExternalLink size={12} /> {o.error}</p>}
          </div>
        )}
      </div>
    </div>
  );
}

export default function AdminCommerceOrders() {
  useAdmin();
  const [rows, setRows] = useState(null);
  const [tab, setTab] = useState("");
  const [sel, setSel] = useState(null);

  const load = useCallback(() => { api.admOrders({ status: tab || undefined }).then(setRows).catch(() => setRows([])); }, [tab]);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="p-6 md:p-8" data-testid="admin-commerce-orders">
      <div><span className={OVERLINE}>Commerce</span><h1 className={`${H1} mt-1`}>Orders</h1></div>
      <p className="text-sm text-[#5C6B61] mt-2">Real HomeRange Goods orders. On-hold orders are created in Printify but only submitted for production once Stripe is live.</p>

      <div className="flex gap-1.5 mt-5 mb-4 flex-wrap">
        {TABS.map(([k, l]) => <button key={k} onClick={() => setTab(k)} className={`px-3 py-1.5 text-xs border ${tab === k ? "bg-[#1F2A22] text-white border-[#1F2A22]" : "border-[#E5E2DC] text-[#5C6B61]"}`} data-testid={`orders-tab-${k || "all"}`}>{l}</button>)}
      </div>

      {rows === null ? (
        <div className="py-16 grid place-items-center"><Loader2 className="animate-spin text-[#FF6A00]" /></div>
      ) : rows.length === 0 ? (
        <div className="py-12 text-center text-[#5C6B61]"><Package size={34} className="mx-auto text-[#C7C2B8]" /><p className="mt-3">No orders yet.</p></div>
      ) : (
        <div className="border border-[#E5E2DC] bg-white overflow-x-auto" data-testid="orders-list">
          <table className="w-full text-sm">
            <thead><tr className="text-left text-[11px] uppercase tracking-wide text-[#8A8478] border-b border-[#E5E2DC]">
              <th className="px-4 py-2.5">Order</th><th className="px-4 py-2.5">Customer</th><th className="px-4 py-2.5">Total</th><th className="px-4 py-2.5">Shipping</th><th className="px-4 py-2.5">Absorbed</th><th className="px-4 py-2.5">Status</th>
            </tr></thead>
            <tbody>
              {rows.map((o) => (
                <tr key={o.session_id} onClick={() => setSel(o.session_id)} className="border-b border-[#F0EEE9] last:border-0 cursor-pointer hover:bg-[#F9F8F6]" data-testid={`order-row-${o.session_id}`}>
                  <td className="px-4 py-2.5 font-mono text-xs text-[#1F2A22]">…{o.session_id.slice(-10)}</td>
                  <td className="px-4 py-2.5 text-[#5C6B61]">{o.email}</td>
                  <td className="px-4 py-2.5 text-[#1F2A22]">{money(o.total_cents)}</td>
                  <td className="px-4 py-2.5 text-[#5C6B61]">{o.free_shipping ? "Free" : money(o.shipping_cents)}</td>
                  <td className="px-4 py-2.5">{o.shipping_absorbed_cents > 0 ? <span className="text-[#B04A3A]">{money(o.shipping_absorbed_cents)}</span> : <span className="text-[#C7C2B8]">—</span>}</td>
                  <td className="px-4 py-2.5"><StatusPill s={o.status} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {sel && <OrderDrawer sid={sel} onClose={() => setSel(null)} />}
    </div>
  );
}
